'use client'

import { useEffect } from 'react'
import type { RefObject } from 'react'

/** Desplazamiento mínimo (px) para que un pulsado cuente como arrastre y no
 * como click: por debajo, el click sobre la tarjeta navega con normalidad. */
const DRAG_THRESHOLD = 5

/**
 * Arrastre con ratón de un carril horizontal desplazable (`overflow-x: auto`).
 *
 * El gesto táctil ya desplaza el carril de forma nativa, pero con ratón el
 * navegador no lo hace: sin esto, en escritorio solo se podía avanzar con las
 * flechas o con la rueda/trackpad. Solo actúa con `pointerType === 'mouse'`
 * (táctil y lápiz siguen con el scroll nativo).
 *
 * - La captura del puntero se toma solo al superar `DRAG_THRESHOLD`: tomarla
 *   en `pointerdown` redirigiría el `click` al carril y las tarjetas (enlaces)
 *   dejarían de navegar con un click simple.
 * - Tras un arrastre real se anula el `click` siguiente (fase de captura) para
 *   no navegar al soltar sobre una tarjeta.
 * - Al empezar el arrastre se desactiva `scroll-snap-type` (el snap pelearía
 *   con cada `scrollLeft` asignado) y ya no se reactiva: hacerlo al soltar
 *   recolocaba el carril en la tarjeta más próxima (salto visible).
 * - Se cancela el `dragstart` nativo de imágenes/enlaces, que si no
 *   "arrastraría" la imagen fantasma en vez del carril.
 *
 * `itemsLength` (mismo criterio que `useRailScroll`): vuelve a enlazar los
 * listeners cuando cambia el número de tarjetas — un carril que se monta más
 * tarde (p. ej. `RecentlyViewed`, que no pinta nada hasta tener ítems) no
 * existe aún en el primer efecto.
 */
export function useDragScroll(railRef: RefObject<HTMLElement | null>, itemsLength: number): void {
  useEffect(() => {
    const el = railRef.current
    if (!el) return

    let pointerId: number | null = null
    let startX = 0
    let startScrollLeft = 0
    let dragging = false

    const onPointerDown = (event: PointerEvent): void => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return
      pointerId = event.pointerId
      startX = event.clientX
      startScrollLeft = el.scrollLeft
      dragging = false
    }

    const onPointerMove = (event: PointerEvent): void => {
      if (event.pointerId !== pointerId) return
      const dx = event.clientX - startX
      if (!dragging) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return
        dragging = true
        el.setPointerCapture(event.pointerId)
        el.style.scrollSnapType = 'none'
        el.style.cursor = 'grabbing'
      }
      el.scrollLeft = startScrollLeft - dx
    }

    const endDrag = (event: PointerEvent): void => {
      if (event.pointerId !== pointerId) return
      pointerId = null
      if (!dragging) return
      if (el.hasPointerCapture(event.pointerId)) el.releasePointerCapture(event.pointerId)
      // `scroll-snap-type` NO se restaura: al reactivarlo el navegador
      // recoloca el carril en el punto de snap más próximo y se percibe como
      // un salto. Tras un arrastre con ratón el carril se queda donde lo
      // soltó el usuario (el táctil nunca pasa por aquí y conserva el snap).
      el.style.cursor = ''
    }

    const onClickCapture = (event: MouseEvent): void => {
      if (!dragging) return
      dragging = false
      event.preventDefault()
      event.stopPropagation()
    }

    const onDragStart = (event: DragEvent): void => {
      event.preventDefault()
    }

    el.addEventListener('pointerdown', onPointerDown)
    el.addEventListener('pointermove', onPointerMove)
    el.addEventListener('pointerup', endDrag)
    el.addEventListener('pointercancel', endDrag)
    el.addEventListener('click', onClickCapture, true)
    el.addEventListener('dragstart', onDragStart)

    return () => {
      el.removeEventListener('pointerdown', onPointerDown)
      el.removeEventListener('pointermove', onPointerMove)
      el.removeEventListener('pointerup', endDrag)
      el.removeEventListener('pointercancel', endDrag)
      el.removeEventListener('click', onClickCapture, true)
      el.removeEventListener('dragstart', onDragStart)
    }
  }, [railRef, itemsLength])
}
